import type { JSX } from 'react';
import type { Site } from '@/data/schemas';
import { DataList, Panel } from '@/components/blocks/data-display';
import { generateDisplaySections } from '@/utils/generateDisplaySections';
import { siteFormFields, siteSections } from './siteFormSchema';
import { formatIsoDateDDMMYYYY, formatMonthYear } from '@/utils/date';

/**
 * Format a string field value, returning "—" for empty/null values
 */
function formatField(value: string | null | undefined): string {
  if (!value) return '—';
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : '—';
}

function formatIsoDateLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return formatIsoDateDDMMYYYY(value) ?? '—';
}

function formatMonthYearLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return formatMonthYear(value, 'it-IT') ?? '—';
}

function formatBoolean(value: boolean | null | undefined): string {
  if (value === true) return 'Sì';
  if (value === false) return 'No';
  return '—';
}

export interface SiteAnagraficaProps {
  /** Site data to display */
  site: Site | null | undefined;
}

/**
 * Encapsulated anagrafica component for site details.
 * Fully programmatic - all sections auto-generated from schema.
 */
export function SiteAnagrafica({ site }: SiteAnagraficaProps): JSX.Element {
  // Auto-generate all sections from schema
  const sections = generateDisplaySections(
    site as Record<string, unknown> | undefined,
    siteFormFields,
    siteSections,
    {
      text: (v) => formatField(v as string | null | undefined),
      date: (v) => formatIsoDateLabel(v as string | null | undefined),
      boolean: (v) => formatBoolean(v as boolean | null | undefined),
      number: (v) => (v != null ? String(v) : '—'),
      'month-year': (v) => formatMonthYearLabel(v as string | null | undefined),
    }
  );

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {sections.map((section) => (
        <Panel key={section.key} title={section.title}>
          <DataList items={section.items} />
        </Panel>
      ))}
    </div>
  );
}
